import {
  DragDropContext,
  DragDropProvider,
  DragDropState,
  DropTarget,
  useDragDropContext,
} from '@wandb/weave/common/containers/DragDropContainer';
import {findIndex, isEqual} from 'lodash';
import React, {
  CSSProperties,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Virtuoso, VirtuosoHandle} from 'react-virtuoso';

import {useIsSingleRun} from '../../pages/Run/RunWorkspaceRunContext';
import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelBankConfigActions from '../../state/views/panelBankConfig/actions';
import * as PanelBankConfigTypes from '../../state/views/panelBankConfig/types';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {
  getMousePositionMemoized,
  getMousePositionRelativeTo,
} from '../../util/mouse';
import {isPanel, isPanelBankSection} from '../../util/panelbank';
import {useFirstTimingEventWhen} from '../../util/profiler/react';
import {useKeyInfoQueryContext} from '../MultiRunWorkspace/KeyInfoQueryContext';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {SectionSettingsContextProvider} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {AddSectionButton} from './AddSectionButton';
import {
  EmptyPanelBankMessage,
  EmptyPanelSearchMessage,
  EmptySectionMessageNoMetrics,
} from './EmptyMessages';
import {
  isSectionOpen,
  usePanelBankSectionsOpenTempContext,
} from './hooks/panelBankSectionIsOpenHooks';
import usePinnedSections from './hooks/usePinnedSections';
import {useScrollToPanelBankSectionId} from './hooks/useScrollToPanelBankSectionId';
import {useVisibleSectionsContext} from './hooks/useVisibleSectionsContext';
import {PanelBankSection} from './PanelBankSection';
import {
  PanelBankConfigState,
  PanelBankConfigWithRefs,
  PanelBankProps,
} from './types';

const MARGIN_DRAG_HANDLE_ICON = 12;
const SECTION_TITLE_HEIGHT = 49;

type PanelBankSectionsProps = PanelBankProps & {
  panelBankConfigWithRefs: PanelBankConfigWithRefs; // TODO: get this from context
  panelBankSectionsDomRef: RefObject<HTMLDivElement>;
};

// This component renders the Panelbank sections (everything besides search + options bar)
// Most of the PanelBank logic lives here
export default function PanelBankSections(props: PanelBankSectionsProps) {
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const {moveSectionBefore, movePanel} =
    usePanelBankConfigActions(panelBankConfigRef);

  const {
    isProjectReadOnly,
    panelBankConfigWithRefs,
    panelBankSectionsDomRef,
    pinPanelBankSection,
  } = props;
  const isSingleRun = useIsSingleRun();

  const {debouncedSearchRegex} = usePanelSearchDebouncedContext();
  const isSearching = !!debouncedSearchRegex;

  const lastShiftKeyRef = useRef(false);
  const {
    keyInfoQuery: {historyKeyInfo},
  } = useKeyInfoQueryContext();

  const visibleSectionsWithRefs = useVisibleSectionsContext();
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);

  useScrollToPanelBankSectionId(virtuosoRef);

  const {isSectionOpenWhileSearching} = usePanelBankSectionsOpenTempContext();

  const {dragRef, shiftKey} = useDragDropContext();

  const [firstSectionIdx] = useState(
    visibleSectionsWithRefs.findIndex(s =>
      isSectionOpen(
        s.__id__,
        s.isOpen,
        isSearching,
        isSectionOpenWhileSearching,
        dragRef,
        shiftKey
      )
    )
  );
  const newPinnedSections = usePinnedSections(visibleSectionsWithRefs);

  const {defaultMoveToSectionName} = panelBankConfigWithRefs.settings;
  const defaultMoveToSection = panelBankConfigWithRefs.sections.find(s => {
    return s.name === defaultMoveToSectionName;
  });

  // While dragging sections, all sections are collapsed.
  // This function adds a spacer to account for the height difference.
  const getTopSpacerHeightWhenDraggingSection = useCallback(
    (dragDropContext: DragDropState) => {
      const draggingSectionIndex = findIndex(visibleSectionsWithRefs, s => {
        return s.ref.id === dragDropContext.dragRef?.id;
      });
      const panelBankSectionsElement = panelBankSectionsDomRef.current;
      const mousePosition =
        dragDropContext.mouseDownEvent != null &&
        panelBankSectionsElement != null
          ? getMousePositionRelativeTo(
              dragDropContext.mouseDownEvent,
              panelBankSectionsElement
            )
          : null;

      const result =
        draggingSectionIndex > 0 && mousePosition != null
          ? mousePosition.client.y -
            draggingSectionIndex * SECTION_TITLE_HEIGHT -
            mousePosition.offset.y -
            MARGIN_DRAG_HANDLE_ICON
          : 0;

      return result;
    },
    [visibleSectionsWithRefs, panelBankSectionsDomRef]
  );

  // Log to dd time it took for workspace to become visible
  useFirstTimingEventWhen('workspace_visible', () => true);

  // Log to dd if there are no open sections
  useFirstTimingEventWhen(
    'workspace_no_open_sections',
    () => firstSectionIdx === -1
  );

  if (
    panelBankConfigWithRefs.state !== PanelBankConfigState.Ready &&
    visibleSectionsWithRefs.every(s => s.activePanelRefs.length === 0) &&
    historyKeyInfo.lastStep != null &&
    historyKeyInfo.lastStep < 0
  ) {
    return (
      <>
        <div className="no-metrics-logged">
          <EmptySectionMessageNoMetrics />
        </div>
        <div className="panel-bank__divider" />
        <div style={{padding: 16}}>
          <AddSectionButton />
        </div>
      </>
    );
  }

  const getTopSpacerHeightWhenDraggingPanel = (
    dragDropContext: DragDropState
  ) => {
    const sectionIndexOnShift = findIndex(visibleSectionsWithRefs, s => {
      return s.ref.id === dragDropContext.dropRefOnShift?.id;
    });
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    const mouseYOnShift = dragDropContext.mouseEventOnShift?.pageY!;
    const result =
      sectionIndexOnShift > -1
        ? mouseYOnShift -
          72 - // panelbank top padding
          52 - // nav bar height
          (isSingleRun ? 44 : 0) - // run workspace run name header
          (sectionIndexOnShift + 0.5) * SECTION_TITLE_HEIGHT
        : 0;
    return result;
  };

  const scrollToElementOnShiftRelease = (dragDropContext: DragDropState) => {
    if (
      dragDropContext.elementOnShiftRelease != null &&
      dragDropContext.mouseEventOnShiftRelease != null
    ) {
      window.scrollTo({
        top:
          (dragDropContext.elementOnShiftRelease as any).offsetTop +
          72 +
          52 -
          getMousePositionMemoized(dragDropContext.mouseEventOnShiftRelease)
            .client.y +
          25,
      });
    }
  };

  // This key forces <Virtuoso> to re-render if a panel is deleted.
  // (Otherwise it'll use a stale visibleSectionsWithRefs and crash.)
  // I think the real fix is to use the <GroupedVirtuoso> component, but that'll involve some refactoring.
  const virtuosoKey = visibleSectionsWithRefs
    .map(s => s.panels.length)
    .join('-');

  return (
    <>
      <DragDropProvider>
        <DragDropContext.Consumer>
          {dragDropContext => {
            const isDraggingPanel =
              dragDropContext.dragRef != null &&
              isPanel(dragDropContext.dragRef);
            const isDraggingSection =
              dragDropContext.dragRef != null &&
              isPanelBankSection(dragDropContext.dragRef);

            if (!dragDropContext.shiftKey && lastShiftKeyRef.current) {
              setTimeout(() => {
                scrollToElementOnShiftRelease(dragDropContext);
              });
            }
            lastShiftKeyRef.current = dragDropContext.shiftKey;

            const shouldRenderPanelBankSpacer =
              ((isDraggingPanel && dragDropContext.shiftKey) ||
                isDraggingSection) &&
              dragDropContext.mouseDownEvent != null;

            return (
              <>
                {shouldRenderPanelBankSpacer && (
                  <PanelBankSpacer
                    dropTargetSectionRef={visibleSectionsWithRefs[0].ref}
                    onDrop={() => {
                      if (isDraggingSection) {
                        moveSectionBefore(
                          dragDropContext.dragRef as PanelBankSectionConfigTypes.Ref,
                          visibleSectionsWithRefs[0].ref
                        );
                      }
                    }}
                    style={{
                      height: isDraggingSection
                        ? getTopSpacerHeightWhenDraggingSection(dragDropContext)
                        : getTopSpacerHeightWhenDraggingPanel(dragDropContext),
                    }}
                  />
                )}
                <Virtuoso
                  style={{flexGrow: 1}}
                  ref={virtuosoRef}
                  // These values are somewhat arbitrary. General goal here is that
                  // when users in small workspaces scroll down then up, we don't re-load data
                  // for charts that were previously loaded when the user was above.
                  // we have to make a trade off here between over rendering components, which can
                  // significantly slow down the page, and not losing data that the user had
                  // previously loaded. We previously had this set to 20000, but that caused
                  // noticeable rendering lag so here we reduce it to 1000 to improve performance
                  // at the cost of some increased need for reloading data when the user scrolls
                  // back up.
                  increaseViewportBy={{top: 1000, bottom: 1000}}
                  key={virtuosoKey}
                  data={visibleSectionsWithRefs}
                  useWindowScroll
                  components={{
                    Footer: () => {
                      if (!visibleSectionsWithRefs.length) {
                        return null;
                      }
                      return (
                        <div
                          style={{
                            padding: '1rem 2rem',
                            display: isDraggingSection ? 'none' : 'flex',
                            justifyContent: 'flex-end',
                          }}>
                          <AddSectionButton />
                        </div>
                      );
                    },
                    EmptyPlaceholder: isSearching
                      ? EmptyPanelSearchMessage
                      : EmptyPanelBankMessage,
                  }}
                  itemContent={(idx, renderSectionWithRefs) => {
                    const sectionRef = renderSectionWithRefs.ref;
                    return (
                      <SectionSettingsContextProvider sectionRef={sectionRef}>
                        <PanelBankSection
                          key={sectionRef.id}
                          isProjectReadOnly={isProjectReadOnly}
                          panelBankSectionConfigRef={sectionRef}
                          //// TODO: probably move to context, but remember report grids
                          activePanelRefs={
                            renderSectionWithRefs.activePanelRefs
                          }
                          inactivePanelRefs={
                            renderSectionWithRefs.inactivePanelRefs
                          }
                          panelSearchResults={
                            renderSectionWithRefs.panelSearchResults
                          }
                          panelSearchMatchCount={
                            renderSectionWithRefs.panelSearchMatchCount
                          }
                          isSectionNameSearchMatch={
                            renderSectionWithRefs.isSectionNameSearchMatch
                          }
                          /////
                          moveSectionBefore={moveSectionBefore}
                          // deletePanel={panelRef =>
                          //   deletePanel(panelRef, sectionRef)
                          // }
                          defaultMoveToSectionRef={
                            defaultMoveToSection
                              ? defaultMoveToSection.ref
                              : undefined
                          }
                          movePanel={movePanel}
                          pinPanelBankSection={pinPanelBankSection}
                          isPinned={renderSectionWithRefs.pinned}
                          showPinningBorder={newPinnedSections.includes(
                            sectionRef.id
                          )}
                          isFirstSection={idx === firstSectionIdx}
                        />
                      </SectionSettingsContextProvider>
                    );
                  }}
                />
                {shouldRenderPanelBankSpacer && (
                  <PanelBankSpacer
                    dropTargetSectionRef={
                      visibleSectionsWithRefs[
                        visibleSectionsWithRefs.length - 1
                      ].ref
                    }
                    onDrop={() => {
                      moveSectionBefore(
                        dragDropContext.dragRef as PanelBankSectionConfigTypes.Ref
                      );
                    }}
                    style={{
                      height: '100vh',
                    }}
                  />
                )}
              </>
            );
          }}
        </DragDropContext.Consumer>
      </DragDropProvider>
    </>
  );
}

// All sections collapse to just headers when you're dragging.
// This spacer preserves the height and provides a drop target above+below the sections.
const PanelBankSpacer = (props: {
  dropTargetSectionRef: PanelBankSectionConfigTypes.Ref;
  style: CSSProperties;
  onDrop(): void;
}) => {
  return (
    <DropTarget
      isValidDropTarget={({dragRef}) => {
        return isPanelBankSection(dragRef);
      }}
      partRef={props.dropTargetSectionRef}
      onDrop={({dragRef}) => {
        if (dragRef && !isEqual(dragRef, props.dropTargetSectionRef)) {
          props.onDrop();
        }
      }}
      style={props.style}
    />
  );
};

function usePanelBankConfigActions(
  panelBankConfigRef: PanelBankConfigTypes.Ref
) {
  const moveSectionBefore = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActions.moveSectionBefore
  );
  const movePanel = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActions.movePanel
  );
  return useMemo(() => {
    return {
      moveSectionBefore,
      movePanel,
    };
  }, [moveSectionBefore, movePanel]);
}
